<!--
 * COPYRIGHT Transtron Inc 2023
 *
 * 機能名：会社登録ユーザ情報一覧画面
 *
 * 作成者：2023/08/28 ノア）唐
 * 最終修正者：2023/08/28 ノア）唐
 *
 -->
<template>
  <div class="tti-main-view">
    <!-- ヘッダー -->
    <section>
      <nav class="tti-main-header-shadow">
        <div class="pull-left">
          <el-button
            class="backButton"
            @click="onBackButtonClick"
            v-tooltip="{
              content: `${$t('gpsTracker.tooltipCommon.companyInfo.back')}`,
              placement: 'bottom-start',
              delay: { show: 1000, hide: 0 }
            }"
          >
            <tti-svg-icon icon-name="wo_034"></tti-svg-icon>
          </el-button>
          <div class="tti-viewTitle">
            <tti-svg-icon icon-name="wo_008"></tti-svg-icon>
            <span>{{ $t('gpsTracker.userInfo.managementTitle') }}</span>
          </div>
        </div>
      </nav>
    </section>
    <!-- テーブル -->
    <section class="tti-padding-content">
      <el-table
        ref="tableRef"
        :data="tableData"
        height="calc(100vh - 180px)"
        :border="true"
        :stripe="true"
        :row-class-name="tableRowClassName"
        header-row-class-name="tti-master-table-header"
        :empty-text="$t('messages.common.emptyTableData')"
        highlight-current-row
        @current-change="handleCurrentChange"
      >
        <!-- ユーザID-->
        <el-table-column prop="userId" :label="$t('gpsTracker.userInfo.userId')" width="160"> </el-table-column>
        <!-- ユーザ名称 -->
        <el-table-column prop="userName" :label="$t('gpsTracker.userInfo.userName')" width="160"> </el-table-column>
        <!-- 登録日時 -->
        <el-table-column prop="createTime" :label="$t('master.userMaster.createDate')" width="180" align="center">
        </el-table-column>
        <!-- MFA Reset -->
        <el-table-column prop="mfaReset" width="200" align="center">
          <template slot-scope="scope">
            <el-button
              class="tti-btn-default tti-btn-001 mfa-reset"
              @click="onMfaResetClick(scope.row.id)"
              v-tooltip="{
                content: `${$t('gpsTracker.tooltipCommon.managementUser.resetMfa')}`,
                placement: 'bottom-start',
                delay: { show: 1000, hide: 0 }
              }"
              >{{ $t('master.userMaster.mfaReset') }}</el-button
            >
          </template>
        </el-table-column>
      </el-table>
    </section>
    <!-- フッター -->
    <section class="tti-master-bottom-area">
      <div class="tti-bottom-left-area">
        <!-- 追加 -->
        <el-button
          class="tti-btn-default tti-btn-005"
          @click="onAddClick"
          v-tooltip="{
            content: `${$t('gpsTracker.tooltipCommon.userInfo.btnAdd')}`,
            placement: 'bottom-start',
            delay: { show: 1000, hide: 0 }
          }"
          >{{ $t('buttons.insert') }}</el-button
        >
        <!-- 編集 -->
        <el-button
          class="tti-btn-default tti-btn-005"
          @click="onEditClick"
          :disabled="isBTNDisabled"
          v-tooltip="{
            content: `${$t('gpsTracker.tooltipCommon.userInfo.btnEdit')}`,
            placement: 'bottom-start',
            delay: { show: 1000, hide: 0 }
          }"
        >
          {{ $t('buttons.edit') }}
        </el-button>
        <!-- 削除 -->
        <el-button
          class="tti-btn-default tti-btn-002"
          @click="onDeleteClick"
          :disabled="isBTNDisabled"
          v-tooltip="getTooltip(tooltipType.delete)"
        >
          {{ $t('buttons.delete') }}
        </el-button>
      </div>
    </section>
    <ToolManagerRegister
      :companyPrefix="companyPrefix"
      :dataType="dataType"
      :singleSelection="singleSelection"
      :singleSelectionIndex="singleSelectionIndex"
      :tableData="tableData"
      @updateSuccess="updateSuccess"
      @stepPrevFunction="stepPrevFunction"
      @stepNextFunction="stepNextFunction"
      @dialogClose="dialogClose"
    ></ToolManagerRegister>
  </div>
</template>

<script lang="ts">
import { Component, Ref } from 'vue-property-decorator'
import { Form, Table } from 'element-ui'

import TOOL_COMPANY_BASE from '@/views/companyEditTool/TOOL_COMPANY_BASE.vue'
import { i18n } from '@/locales'
import { ToolManagementUser } from '@/models'
import { deleteManagementUserInfo, getToolManagementUserInfoData, mfaReset } from '@/store/apis'
import { msgBoxService } from '@/commons/services/msgBoxService'
import { dialog } from '@/store/modules'
import { DataType } from '@/commons/constants'

@Component({
  name: 'TOOL_MANAGEMENT_REGISTER',
  components: { ToolManagerRegister: () => import('@/components/ToolManagerRegister.vue') }
})
export default class TOOL_MANAGEMENT_REGISTER extends TOOL_COMPANY_BASE {
  /**
   * フォームのref
   */
  @Ref('form') readonly formRef!: Form

  @Ref('tableRef') readonly tableRef!: Table

  /**
   * 画面ID
   */
  pageId = 'TOOL_MANAGEMENT_REGISTER'

  /**
   * 変更したデータ
   */
  tableData: Array<ToolManagementUser> = []

  /**
   * ユーザマスタ編集FORM
   */
  form = {
    tableData: []
  } as Record<string, ToolManagementUser[]>

  /**
   * テーブル行で選択したデータ
   */
  singleSelection: ToolManagementUser = {} as ToolManagementUser

  /**
   * テーブル行で選択したデータINDEX
   */
  singleSelectionIndex = -1

  /**
   * ボタンのアクティブ状態
   */
  isBTNDisabled = true

  /**
   * 契約会社Prefix名
   */
  companyPrefix = ''

  /**
   * 追加、編集
   */
  dataType = DataType.INSERT

  /**
   * インスタンスがマウントされたちょうど後にフック処理
   *
   * @returns {void}
   */
  async mounted(): Promise<void> {
    this.$log.debug('TOOL_MANAGEMENT_REGISTER > mounted')
    this.fetchTableData()
  }

  /**
   * テーブルデータを検索して、初期化を実行する。
   *
   * @returns {Promise<boolean>} 検索実行結果
   */
  async fetchTableData(): Promise<void> {
    try {
      this.$log.debug(`TOOL_MANAGEMENT_REGISTER > fetchTableData`)
      const res = await getToolManagementUserInfoData()
      if (!res) {
        return
      }
      this.tableData = res.list as ToolManagementUser[]
      this.companyPrefix = res.prefix
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
    } catch (e: any) {
      this.$log.debug(`TOOL_MANAGEMENT_REGISTER > fetchTableData: e=`, e)
      throw e
    }
  }

  /**
   * 登録
   *
   */
  onAddClick(): void {
    this.$log.debug('TOOL_MANAGEMENT_REGISTER > onAddClick ')
    this.dataType = DataType.INSERT
    dialog.setToolUserAddUpdateShow(true)
  }

  /**
   * 編集
   *
   */
  onEditClick(): void {
    this.$log.debug('TOOL_MANAGEMENT_REGISTER > onEditClick ')
    this.dataType = DataType.UPDATE
    dialog.setToolUserAddUpdateShow(true)
  }

  /**
   * ダイアログを閉じた後にトリガーされる関数
   *
   * @param {boolean} showStatus ダイアログボックスの表示状態
   */
  dialogClose(showStatus: boolean): void {
    this.$log.debug(`TOOL_MANAGEMENT_REGISTER >  dialogClose `)
    if (showStatus) {
      this.singleSelectionIndex = -1
      this.singleSelection = {} as ToolManagementUser
      this.isBTNDisabled = true
      this.fetchTableData()
    }
  }

  /**
   * ダイアログボックスで次の操作で格納された場所が正常に処理されました
   *
   * @param {number} index インデックス
   */
  async updateSuccess(index: number): Promise<void> {
    this.$log.debug(`TOOL_MANAGEMENT_REGISTER >  updateSuccess `, dialog.isToolUserAddUpdateDialogShow)
    await this.fetchTableData()
    if (index === -1) {
      this.singleSelectionIndex = -1
      this.singleSelection = {} as ToolManagementUser
    }
    if (this.tableData && this.tableData.length > index && index >= 0) {
      this.$nextTick(() => {
        this.singleSelection = this.tableData[index]
        this.singleSelectionIndex = index
      })
    }
  }

  /**
   * テーブルの選択したデータを変更する。
   *
   * @param {ToolManagementUser} row 選択されたデータ
   */
  handleCurrentChange(row: ToolManagementUser): void {
    this.$log.debug(`TOOL_MANAGEMENT_REGISTER >  handleCurrentChange,`, row)
    if (row) {
      this.singleSelection = row
      this.singleSelectionIndex = this.tableData.findIndex(item => item.id === row.id)
      this.isBTNDisabled = false
    } else {
      if (
        (dialog.isToolUserAddUpdateDialogShow && this.dataType == DataType.UPDATE) ||
        this.singleSelectionIndex !== -1
      ) {
        this.isBTNDisabled = false
      } else {
        this.isBTNDisabled = true
        this.singleSelectionIndex = -1
      }
    }
  }

  /**
   * 選択した行の設定
   *
   * @param {number} currentIndex 行索引の選択
   */
  stepPrevFunction(currentIndex: number): void {
    this.$log.debug(`TOOL_MANAGEMENT_REGISTER >  stepPrevFunction,`, currentIndex)

    const changedIndex = currentIndex - 1
    this.changeSelectedRow(changedIndex)
  }

  /**
   * 選択した行の設定
   *
   * @param {number} currentIndex 行索引の選択
   */
  stepNextFunction(currentIndex: number): void {
    this.$log.debug(`TOOL_MANAGEMENT_REGISTER >  stepNextFunction,`, currentIndex)

    const changedIndex = currentIndex + 1
    this.changeSelectedRow(changedIndex)
  }

  /**
   * 選択した行を実施
   *
   * @param {number} selectRowIndex 行索引の選択
   */
  changeSelectedRow(selectRowIndex: number): void {
    this.fetchTableData()
    if (this.tableData && this.tableData.length > selectRowIndex && selectRowIndex >= 0) {
      this.singleSelection = this.tableData[selectRowIndex]

      this.$nextTick(() => {
        this.singleSelectionIndex = selectRowIndex
      })
    }
  }

  /**
   * テーブル行CLASS
   *
   * @param {any} rowData 対象データ
   * @returns {string} 目標CLASS
   */
  /* eslint-disable @typescript-eslint/no-explicit-any */
  // eslint-disable-next-line @typescript-eslint/explicit-module-boundary-types
  tableRowClassName(rowData: any): string {
    const { rowIndex } = rowData

    if (this.singleSelectionIndex === -1) {
      return ''
    }

    if (this.singleSelectionIndex === rowIndex) {
      return 'current-row'
    }

    return ''
  }

  /**
   * 削除
   *
   */
  onDeleteClick(): void {
    this.$log.debug('TOOL_MANAGEMENT_REGISTER > onDeleteClick ')
    const confirmCallback = () => {
      this.doDeleteUserInfo()
      this.singleSelectionIndex = -1
    }

    msgBoxService.confirm(i18n.t('messages.masterCommon.deleteConfirm').toString(), confirmCallback)
  }

  /**
   * ユーザ削除実行
   */
  async doDeleteUserInfo(): Promise<void> {
    if (this.singleSelection) {
      const param = { id: this.singleSelection.id }
      await deleteManagementUserInfo(param)
      this.fetchTableData()
    }
  }

  /**
   * 戻るボタンをクリックした時の処理。
   *
   * @returns {void}
   */
  onBackButtonClick(): void {
    this.$log.debug(`TOOL_MANAGEMENT_REGISTER > onBackButtonClick`)

    this.$router.back()
  }

  /**
   * MFA認証リセット
   *
   * @param {string} id id
   */
  async onMfaResetClick(id: string): Promise<void> {
    this.$log.debug('TOOL_MANAGEMENT_REGISTER > onMfaResetClick ')
    const confirmCallback = async () => {
      const param = { id: id }
      await mfaReset(param)
    }

    msgBoxService.confirm(i18n.t('messages.masterCommon.resetMfaConfirm').toString(), confirmCallback)
  }
}
</script>

<style lang="scss" scoped>
::v-deep .tti-bottom-right-area .tti-btn-default {
  margin-right: 0 !important;
}

.tti-main-header-shadow {
  display: flex;
  flex-direction: row;
  justify-content: space-between;
}

.searchBox {
  display: flex;
  align-items: center;

  ::v-deep .el-input__inner {
    height: 35px;
    line-height: 35px;
    padding: 0 8px;
  }
}

.mfa-reset {
  width: 150px;
}

::v-deep th.gutter {
  display: none;
}
</style>
